import { Box, Card, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { SxProps } from '@mui/system'
import { isNumber, isString } from 'lodash'
import React, { CSSProperties } from 'react'
import { Link } from 'react-router-dom'

import FormatCell from '#/components/FormatCell'
import Colors from '#/styles/Colors'

// Tipagem das props
type PageBuilderCardProps = {
  value?: string | number
  label: string
  link?: string
  level?: 'success' | 'warning' | 'info' | 'danger' | 'neutral' | 'error'
  font?: 'small' | 'normal' | 'large'
  disabled?: boolean
  elevation?: number
  variation?: string
  variationLevel?: keyof typeof variationColors
  labelPosition?: 'top' | 'bottom'
  alignItems?: CSSProperties['alignItems']
  sx?: SxProps
  id?: string
}

// Styled components
const ValueText = styled('div')(({ font }: { font: 'small' | 'normal' | 'large' | undefined }) => ({
  'fontWeight': '700',
  'letterSpacing': '0.5px',
  'margin': 0,
  ...(font === 'small' && {
    fontSize: '15px',
    lineHeight: '15px',
  }),
  ...(font === 'normal' && {
    fontSize: '24px',
    lineHeight: '24px',
  }),
  ...(font === 'large' && {
    fontSize: '32px',
    lineHeight: '32px',
  }),
  '& > *': {
    fontWeight: '700',
  },
}))

const ValueContainer = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  gap: '10px',
})

const variationColors = {
  ...Colors.health,
  ...Colors.feedback,
}

export const VariationBadge = styled(Chip)(
  ({ variationlevel = 'success' }: { variationlevel: keyof typeof variationColors }) => ({
    '& .MuiChip-label': {
      color: variationColors?.[variationlevel]?.color,
      fontSize: '12px',
      padding: '0 8px',
    },
    'backgroundColor': variationColors?.[variationlevel]?.backgroundColor,
    'height': '18px',
  }),
)

const CardLabel = styled('p')({
  fontSize: '14px',
  letterSpacing: '0.5px',
  margin: 0,
  textAlign: 'center',
})

const CardLink = styled(Link)({
  '&:hover': {
    background: 'rgba(0,0,0,0.03)',
  },
  'borderRadius': '16px',
  'display': 'block',
})

// Sub-components
const CardValue = ({
  font,
  variation,
  variationLevel = 'success',
  ...rest
}: Pick<PageBuilderCardProps, 'value' | 'variation' | 'font' | 'variationLevel'>) => {
  let normalizedValue: any = '-'

  // Tipagem pica das galaxias, bj matheus
  const value = rest.value as unknown as any

  /**
   * Se é uma string ou um número, renderiza como string
   */
  if (isString(value) || isNumber(value)) {
    normalizedValue = value
  }

  /**
   * Se é um objeto com a prop `type`, renderiza usando o FormatCell
   */
  if (!isString(value) && !isNumber(value) && value?.type) {
    normalizedValue = FormatCell({ type: value?.type, value: value })
  }

  /**
   * Type não está registrado no FormatCell, então renderiza como string
   */
  if (!normalizedValue && !(isString(value) || isNumber(value))) {
    normalizedValue = '-'
  }

  return (
    <ValueContainer>
      <ValueText className="PageBuilderCardValueText" font={font}>
        {normalizedValue}
      </ValueText>
      {variation && <VariationBadge label={variation} variationlevel={variationLevel} />}
    </ValueContainer>
  )
}

const CardContainer = ({
  alignItems = 'center',
  children,
  disabled = false,
  elevation = 0,
  id,
  labelPosition = 'bottom',
  level = 'neutral',
  sx,
}: Partial<PageBuilderCardProps> & { children: React.ReactNode }) => {
  return (
    <Card
      id={id}
      elevation={elevation}
      sx={{
        '& .PageBuilderCardValueText, & p': {
          color: disabled ? '#CCC' : Colors.feedback?.[level]?.color,
        },
        alignItems,
        'backgroundColor': Colors.feedback?.[level]?.backgroundColor,
        'borderRadius': '16px',
        'display': 'flex',
        'flexDirection': labelPosition === 'top' ? 'column-reverse' : 'column',
        'flexGrow': 1,
        'gap': '8px',
        'height': '100%',
        'padding': '16px',
        ...sx,
      }}
    >
      {children}
    </Card>
  )
}

const CardWrapper = ({
  children,
  link,
}: Pick<PageBuilderCardProps, 'link'> & { children: React.ReactNode }) => {
  return link ? <CardLink to={link}>{children}</CardLink> : <>{children}</>
}

// Main component
export const PageBuilderCard = ({
  font = 'normal',
  label,
  link,
  value,
  variation,
  variationLevel = 'success',
  ...rest
}: PageBuilderCardProps) => {
  return (
    <CardWrapper link={link}>
      <CardContainer {...rest}>
        <CardValue
          value={value}
          variation={variation}
          font={font}
          variationLevel={variationLevel}
        />
        <CardLabel>{label}</CardLabel>
      </CardContainer>
    </CardWrapper>
  )
}
